import { z } from "@/zod";
import type { ZodSchema, ZodObject, ZodRawShape } from "zod";

/** Unwraps recursively nested ZodOptional/ZodNullable/ZodReadonly to get the base type. */
export function unwrapZodType(zodType: z.ZodTypeAny): z.ZodTypeAny {
  const innerType = zodType?._def?.innerType;
  if (!innerType) return zodType;
  return unwrapZodType(innerType);
}

/** Transforms the user input into null when empty string provided */
export const emptyStringToNull = (schema: ZodSchema) =>
  z.preprocess((val) => (val === "" ? null : val), schema);

/** Applies emptyStringToNull to all fields in a zod object schema */
export function applyEmptyStringsToNulls<T extends z.ZodRawShape>(shape: T) {
  return z.object(
    Object.fromEntries(
      Object.entries(shape).map(([key, schema]) => [
        key,
        emptyStringToNull(schema as z.ZodTypeAny),
      ]),
    ) as unknown as T,
  );
}

/** Removes fields with null, undefined, or empty string values before sending to backend */
export function removeNullishFields<T extends ZodObject<ZodRawShape>>(
  schema: T,
) {
  return schema.transform((data) => {
    return Object.fromEntries(
      Object.entries(data).filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      ),
    ) as z.infer<T>;
  });
}
